
import { useEffect, useRef, useState } from "react";

const StreamingPlatforms = () => {
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const scrollContainer = logoContainerRef.current;
    if (!scrollContainer) return;

    let animationFrameId: number;
    let lastTimestamp: number;

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaTime = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (scrollContainer) {
        // Smooth constant speed of 1 pixel per frame
        const scrollSpeed = 2.5;
        
        // If we've scrolled too far right, reset position to create seamless loop
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
          scrollContainer.scrollLeft = 0;
        } else {
          scrollContainer.scrollLeft += scrollSpeed * (deltaTime / 16.67); // Normalize to 60fps
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    // Intersection observer for animation
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (scrollContainer.parentElement) {
      observer.observe(scrollContainer.parentElement);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (scrollContainer.parentElement) {
        observer.unobserve(scrollContainer.parentElement);
      }
    };
  }, []);

  const platformLogos = [
    {
      name: "Resso",
      src: "/assets/logos/resso.svg"
    },
    {
      name: "Jio Saavn",
      src: "/assets/logos/jiosaavn.svg"
    },
    {
      name: "Apple Music",
      src: "/assets/logos/applemusic.svg"
    },
    {
      name: "Amazon Music",
      src: "/assets/logos/amazonmusic.svg"
    },
    {
      name: "Spotify",
      src: "/assets/logos/spotify.svg"
    },
    {
      name: "Anghami",
      src: "/assets/logos/anghami.svg"
    },
    {
      name: "Instagram",
      src: "/assets/logos/instagram.svg"
    },
    {
      name: "YouTube",
      src: "/assets/logos/youtube.svg"
    },
    {
      name: "Deezer",
      src: "/assets/logos/deezer.svg"
    }
  ];

  // Create repeated logo sets for seamless infinite scrolling
  const logoSets = [1, 2, 3];

  return (
    <section className={`py-16 bg-ophira-dark overflow-hidden relative transition-all duration-1000 ${
      isVisible ? 'opacity-100' : 'opacity-80'
    }`}>
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ophira-yellow/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ophira-yellow/20 to-transparent"></div>
      </div>
      
      <div className="container mx-auto px-4 mb-8">
        <div className={`text-center transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="ophira-section-label justify-center">DISTRIBUTION PARTNERS</div>
          <h4 className="text-lg text-ophira-lightgray">
            Distribute your music to all major platforms
          </h4>
        </div>
      </div>
      
      <div className="relative w-full">
        {/* Gradient overlays for fade effect */}
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-ophira-dark to-transparent"></div>
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-ophira-dark to-transparent"></div>
        
        {/* Scrolling container */}
        <div 
          ref={logoContainerRef}
          className="flex overflow-x-scroll no-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex whitespace-nowrap">
            {logoSets.map((set, setIndex) => (
              <div key={setIndex} className="flex items-center">
                {platformLogos.map((logo, logoIndex) => (
                  <div key={`${setIndex}-${logoIndex}`} className="px-8">
                    <div className="h-16 w-48 rounded-lg flex items-center justify-center p-2 transition-all duration-300 hover:bg-ophira-darkgray/50 hover:scale-110">
                      <img 
                        src={logo.src} 
                        alt={logo.name} 
                        className="h-14 w-44 object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StreamingPlatforms;
